import { ReactNode, useEffect, useState } from "react";
import { Close, OpenInNew } from "@mui/icons-material";
import { Box, Fade, IconButton, Link, Modal, Slide, Stack, Tooltip } from "@mui/material";
import { HEADER_HEIGHT } from "~/components/AppHeader";
import EmbedContainer from "~/components/EmbedContainer";
import StickyToolbar from "~/components/StickyToolbar";
import TextOverflow from "~/components/TextOverflow";
import noop from "~/utils/noop";

interface EmbedOverlayProps {
  onClose: () => void;
  iframeSrc?: string;
  loading?: boolean;
  /** Handler for processing messages from the embedded iframe */
  messageHandler?: (event: MessageEvent) => void;
  title?: ReactNode;
  hideOpenInNewTab?: boolean;
}

/**
 * Header component for the EmbedOverlay
 * Displays the title, an "open in new tab" button, and a close button
 */
const Header = ({
  title,
  iframeSrc,
  onClose,
  hideOpenInNewTab,
}: {
  title?: ReactNode;
  iframeSrc?: string;
  onClose?: () => void;
  hideOpenInNewTab: boolean;
}) => {
  return (
    <StickyToolbar
      noBorder
      sx={{
        width: "100%",
        height: HEADER_HEIGHT,
        bgcolor: "transparent",
        color: "common.white",
        justifyContent: "space-between",
        px: 1.5,
      }}
    >
      {title && (
        <TextOverflow
          component="h1"
          sx={{
            fontSize: "1rem",
            fontWeight: 600,
            display: "flex",
            gap: 1,
            minWidth: 0,
            alignItems: "center",
            pointerEvents: "auto",
          }}
        >
          {title}
        </TextOverflow>
      )}
      <Box sx={{ pointerEvents: "auto", display: "flex", gap: 1, ml: "auto" }}>
        {iframeSrc && !hideOpenInNewTab && (
          <Tooltip title="Open in new tab">
            <IconButton
              component={Link}
              href={iframeSrc}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: "inherit" }}
              size="small"
            >
              <OpenInNew />
            </IconButton>
          </Tooltip>
        )}
        <IconButton sx={{ color: "inherit", mr: -0.5 }} size="small" onClick={onClose}>
          <Close />
        </IconButton>
      </Box>
    </StickyToolbar>
  );
};

/**
 * A modal overlay that embeds external content in an iframe with a header.
 * Provides loading state management and message handling for iframe communication.
 */
const EmbedOverlay = ({
  iframeSrc,
  loading,
  onClose,
  messageHandler = noop,
  title,
  hideOpenInNewTab = false,
}: EmbedOverlayProps) => (
  <Modal
    open
    onClose={onClose}
    slotProps={{
      backdrop: {
        // Semi-transparent dark backdrop with slight blur effect
        sx: { bgcolor: "rgba(0, 0, 0, 0.7)", backdropFilter: "blur(1px)" },
      },
    }}
  >
    <Stack
      sx={{
        width: "100%",
        height: "100%",
        minHeight: "100vh",
        alignItems: "center",
        outline: "none",
        // Disable pointer events on the stack to allow clicks to pass through to the backdrop
        pointerEvents: "none",
      }}
    >
      <Fade in appear timeout={400} easing={{ enter: "cubic-bezier(0.4, 0, 0.2, 1)" }}>
        <Box width="100%">
          <Header
            title={title}
            iframeSrc={iframeSrc}
            onClose={onClose}
            hideOpenInNewTab={hideOpenInNewTab}
          />
        </Box>
      </Fade>
      <Fade in appear timeout={300} easing={{ enter: "cubic-bezier(0.4, 0, 0.2, 1)" }}>
        <Slide
          direction="up"
          in
          appear
          timeout={300}
          easing={{ enter: "cubic-bezier(0.4, 0, 0.2, 1)" }}
        >
          <Box
            sx={{
              width: "100%",
              minHeight: `calc(100vh - ${HEADER_HEIGHT})`,
              overflow: "auto",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              bgcolor: "background.paper",
              // Re-enable pointer events for children
              "> *": {
                pointerEvents: "auto",
              },
            }}
          >
            <EmbedContainer
              iframeSrc={iframeSrc}
              loading={loading}
              messageHandler={messageHandler}
            />
          </Box>
        </Slide>
      </Fade>
    </Stack>
  </Modal>
);

export default EmbedOverlay;
